import { useEffect, useState, useCallback } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  KeyRound,
  X,
  Search,
  Shield,
  User as UserIcon,
  Wallet,
} from 'lucide-react';
import { authService } from '../../services/auth';
import type { SafeUser } from '../../services/types';

const ALL_SITES = ['witelab', 'ai', 'mockup', 'daily', 'ozon'];
const SITE_LABELS: Record<string, string> = {
  witelab: 'Witelab',
  ai: 'AI Lab',
  mockup: 'Mockup Lab',
  daily: 'Daily Lab',
  ozon: 'Ozon Lab',
};

const AI_MODELS = [
  { value: 'gemini-3.1-pro-image-preview', label: '3.1 Pro Image' },
  { value: 'gemini-3-pro-image-preview', label: '3 Pro Image' },
  { value: 'gemini-3.1-flash-image-preview', label: '3.1 Flash Image' },
  { value: 'gemini-3-flash-preview', label: '3 Flash' },
  { value: 'gemini-2.5-flash-image', label: '2.5 Flash Image' },
];

// ─── Main ────────────────────────────────────────────────────────────

export function UsersPage() {
  const [users, setUsers] = useState<SafeUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SafeUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [passwordFor, setPasswordFor] = useState<SafeUser | null>(null);
  const [balanceFor, setBalanceFor] = useState<SafeUser | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authService.apiFetch('/api/auth/users?limit=500');
      const data = await res.json();
      if (data.success) {
        setUsers(data.data.users);
        setTotal(data.data.total);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (user: SafeUser) => {
    if (user.id === 'admin') return;
    if (!confirm(`Удалить пользователя «${user.username}»?`)) return;

    const res = await authService.apiFetch(`/api/auth/users/${user.id}`, {
      method: 'DELETE',
    });
    const data = await res.json();
    if (data.success) fetchUsers();
  };

  const filtered = search
    ? users.filter(
        (u) =>
          u.username.toLowerCase().includes(search.toLowerCase()) ||
          u.id.toLowerCase().includes(search.toLowerCase()),
      )
    : users;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Пользователи</h1>
          <p className="text-sm text-slate-500 mt-1">Всего: {total}</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-indigo-600 hover:bg-indigo-500 text-white transition"
        >
          <Plus className="w-4 h-4" />
          Создать
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по имени или ID..."
          className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-800/60 border border-white/[0.06] text-slate-200 placeholder-slate-600 focus:outline-none focus:border-indigo-500/40 text-sm"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-slate-800/40 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-white/[0.06] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/60 text-slate-400">
                <th className="text-left px-4 py-3 font-medium">Пользователь</th>
                <th className="text-left px-4 py-3 font-medium">Роль</th>
                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Доступ к сайтам</th>
                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Модели AI</th>
                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Баланс</th>
                <th className="text-left px-4 py-3 font-medium hidden xl:table-cell">Создан</th>
                <th className="text-right px-4 py-3 font-medium">Действия</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => (
                <tr
                  key={user.id}
                  className="border-t border-white/[0.04] hover:bg-white/[0.02] transition"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          user.role === 'admin'
                            ? 'bg-indigo-500/20 text-indigo-400'
                            : 'bg-slate-700 text-slate-300'
                        }`}
                      >
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="text-slate-200 font-medium">{user.username}</span>
                        <p className="text-xs text-slate-600">{user.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'admin'
                          ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                          : 'bg-slate-700/50 text-slate-400 border border-white/[0.06]'
                      }`}
                    >
                      {user.role === 'admin' ? (
                        <Shield className="w-3 h-3" />
                      ) : (
                        <UserIcon className="w-3 h-3" />
                      )}
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="flex gap-1 flex-wrap">
                      {user.allowed_sites.map((s) => (
                        <span
                          key={s}
                          className="px-1.5 py-0.5 text-xs rounded bg-slate-700/50 text-slate-400"
                        >
                          {SITE_LABELS[s] || s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {user.allowed_models === null ? (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-emerald-500/10 text-emerald-400">
                        все
                      </span>
                    ) : (
                      <div className="flex gap-1 flex-wrap">
                        {user.allowed_models.map((m) => {
                          const model = AI_MODELS.find((am) => am.value === m);
                          return (
                            <span
                              key={m}
                              className="px-1.5 py-0.5 text-xs rounded bg-purple-500/10 text-purple-400"
                            >
                              {model?.label || m}
                            </span>
                          );
                        })}
                        {user.allowed_models.length === 0 && (
                          <span className="text-xs text-slate-600">нет доступа</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className={`text-sm font-medium ${
                      (user.balance ?? 0) > 0 ? 'text-emerald-400' : 'text-slate-600'
                    }`}>
                      {(user.balance ?? 0).toFixed(2)} ₽
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell text-slate-500 text-xs">
                    {new Date(user.created_at).toLocaleDateString('ru-RU')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditing(user)}
                        className="p-1.5 rounded-md text-slate-500 hover:text-indigo-400 hover:bg-indigo-500/10 transition"
                        title="Редактировать"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setBalanceFor(user)}
                        className="p-1.5 rounded-md text-slate-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition"
                        title="Баланс"
                      >
                        <Wallet className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setPasswordFor(user)}
                        className="p-1.5 rounded-md text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 transition"
                        title="Сменить пароль"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        disabled={user.id === 'admin'}
                        className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition disabled:opacity-20 disabled:cursor-not-allowed"
                        title="Удалить"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-600">
                    Пользователи не найдены
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {creating && (
        <UserModal
          onClose={() => setCreating(false)}
          onSaved={fetchUsers}
        />
      )}
      {editing && (
        <UserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={fetchUsers}
        />
      )}
      {passwordFor && (
        <PasswordModal
          user={passwordFor}
          onClose={() => setPasswordFor(null)}
        />
      )}
      {balanceFor && (
        <BalanceModal
          user={balanceFor}
          onClose={() => setBalanceFor(null)}
          onSaved={fetchUsers}
        />
      )}
    </div>
  );
}

// ─── User Create/Edit Modal ──────────────────────────────────────────

function UserModal({
  user,
  onClose,
  onSaved,
}: {
  user?: SafeUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!user;
  const [id, setId] = useState(user?.id || '');
  const [username, setUsername] = useState(user?.username || '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>(user?.role || 'user');
  const [allowedSites, setAllowedSites] = useState<string[]>(
    user?.allowed_sites || [...ALL_SITES],
  );
  const [allowedModels, setAllowedModels] = useState<string[] | null>(
    user?.allowed_models ?? null,
  );
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleModel = (model: string) => {
    if (allowedModels === null) {
      // switching from "all" to specific — start with just this model
      setAllowedModels([model]);
      return;
    }
    setAllowedModels((prev) => {
      if (!prev) return [model];
      return prev.includes(model) ? prev.filter((m) => m !== model) : [...prev, model];
    });
  };

  const toggleSite = (site: string) => {
    setAllowedSites((prev) =>
      prev.includes(site) ? prev.filter((s) => s !== site) : [...prev, site],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (isEdit) {
        const res = await authService.apiFetch(`/api/auth/users/${user!.id}`, {
          method: 'PUT',
          body: JSON.stringify({ username, role, allowed_sites: allowedSites, allowed_models: allowedModels }),
        });
        const data = await res.json();
        if (!data.success) {
          setError(data.error);
          setSaving(false);
          return;
        }
      } else {
        if (!id || !password) {
          setError('ID и пароль обязательны');
          setSaving(false);
          return;
        }
        const res = await authService.apiFetch('/api/auth/users', {
          method: 'POST',
          body: JSON.stringify({
            id,
            username,
            password,
            role,
            allowed_sites: allowedSites,
            allowed_models: allowedModels,
          }),
        });
        const data = await res.json();
        if (!data.success) {
          setError(data.error);
          setSaving(false);
          return;
        }
      }

      onSaved();
      onClose();
    } catch {
      setError('Ошибка сервера');
    }
    setSaving(false);
  };

  return (
    <ModalWrapper onClose={onClose} title={isEdit ? 'Редактировать' : 'Новый пользователь'}>
      <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        {!isEdit && (
          <Field label="ID (уникальный)">
            <input
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              className="input"
              placeholder="user_123"
              required
            />
          </Field>
        )}

        <Field label="Имя пользователя">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="input"
            required
          />
        </Field>

        {!isEdit && (
          <Field label="Пароль">
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
              minLength={3}
            />
          </Field>
        )}

        <Field label="Роль">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'user')}
            className="input"
          >
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
        </Field>

        <Field label="Доступ к сайтам">
          <div className="flex gap-2 flex-wrap">
            {ALL_SITES.map((site) => (
              <button
                key={site}
                type="button"
                onClick={() => toggleSite(site)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                  allowedSites.includes(site)
                    ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30'
                    : 'bg-slate-800 text-slate-500 border-white/[0.06] hover:border-white/[0.12]'
                }`}
              >
                {SITE_LABELS[site]}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Модели AI Lab">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setAllowedModels(allowedModels === null ? [] : null)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                allowedModels === null
                  ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                  : 'bg-slate-800 text-slate-500 border-white/[0.06] hover:border-white/[0.12]'
              }`}
            >
              Все модели
            </button>
            {allowedModels !== null && (
              <div className="flex gap-2 flex-wrap">
                {AI_MODELS.map((model) => (
                  <button
                    key={model.value}
                    type="button"
                    onClick={() => toggleModel(model.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                      allowedModels.includes(model.value)
                        ? 'bg-purple-500/15 text-purple-400 border-purple-500/30'
                        : 'bg-slate-800 text-slate-500 border-white/[0.06] hover:border-white/[0.12]'
                    }`}
                  >
                    {model.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Field>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 transition text-sm"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition text-sm disabled:opacity-50"
          >
            {saving ? 'Сохранение...' : isEdit ? 'Сохранить' : 'Создать'}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}

// ─── Balance Modal ───────────────────────────────────────────────────

function BalanceModal({
  user,
  onClose,
  onSaved,
}: {
  user: SafeUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [balance, setBalance] = useState(String(user.balance ?? 0));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const num = parseFloat(balance);
    if (isNaN(num) || num < 0) {
      setError('Введите корректную сумму');
      return;
    }

    setSaving(true);
    try {
      const res = await authService.apiFetch(`/api/auth/users/${user.id}/balance`, {
        method: 'PUT',
        body: JSON.stringify({ balance: num }),
      });
      const data = await res.json();
      setSaving(false);

      if (data.success) {
        setDone(true);
        onSaved();
        setTimeout(onClose, 1000);
      } else {
        setError(data.error || 'Ошибка');
      }
    } catch {
      setError('Ошибка сервера');
      setSaving(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose} title={`Баланс: ${user.username}`}>
      {done ? (
        <div className="py-6 text-center text-emerald-400 text-sm">Баланс обновлён</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="p-3 rounded-lg bg-slate-800/60 border border-white/[0.06] text-sm">
            <span className="text-slate-500">Текущий баланс: </span>
            <span className={`font-medium ${(user.balance ?? 0) > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>
              {(user.balance ?? 0).toFixed(2)} ₽
            </span>
          </div>

          <Field label="Новый баланс (₽)">
            <input
              type="number"
              step="0.01"
              min="0"
              value={balance}
              onChange={(e) => setBalance(e.target.value)}
              className="input"
              autoFocus
            />
          </Field>

          <div className="flex gap-2 flex-wrap">
            {[100, 500, 1000, 5000].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setBalance(String((parseFloat(balance) || 0) + preset))}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition"
              >
                +{preset} ₽
              </button>
            ))}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 transition text-sm"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition text-sm disabled:opacity-50"
            >
              {saving ? 'Сохранение...' : 'Установить'}
            </button>
          </div>
        </form>
      )}
    </ModalWrapper>
  );
}

// ─── Password Modal ──────────────────────────────────────────────────

function PasswordModal({
  user,
  onClose,
}: {
  user: SafeUser;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    const res = await authService.apiFetch(`/api/auth/users/${user.id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
    const data = await res.json();
    setSaving(false);

    if (data.success) {
      setDone(true);
      setTimeout(onClose, 1200);
    } else {
      setError(data.error || 'Ошибка');
    }
  };

  return (
    <ModalWrapper onClose={onClose} title={`Пароль: ${user.username}`}>
      {done ? (
        <div className="py-6 text-center text-emerald-400 text-sm">Пароль изменён</div>
      ) : (
        <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}
          <Field label="Новый пароль">
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
              minLength={3}
              autoFocus
            />
          </Field>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700 transition text-sm"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white transition text-sm disabled:opacity-50"
            >
              {saving ? 'Сохранение...' : 'Изменить'}
            </button>
          </div>
        </form>
      )}
    </ModalWrapper>
  );
}

// ─── Shared UI ───────────────────────────────────────────────────────

function ModalWrapper({
  onClose,
  title,
  children,
}: {
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-white/[0.08] bg-slate-900 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 text-slate-500 hover:text-slate-300 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm text-slate-400 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
